import { memo } from 'react';
import type { RefObject } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import { SDropdownMenu } from '@setvi/shared/components';
import { QuoteProductDTO } from '@setvi/shared/services/react-query';
import { COLUMN_WIDTHS } from '../column-widths';
import { ProductInfo } from './product-info';
import { QuantityColumn, SellColumn, CostColumn, GpColumn } from './columns';
import { FirstRowBox, RowContainer, SecondRowBox } from './styled';
import { ProductOptions } from './product-options';
import { useProductMenuItems } from './hooks/use-product-menu-items';
import { useOptionHandlers } from './hooks/use-option-handlers';

const useStyles = makeStyles(() => ({
  rowContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    padding: '12px',
    width: 'fit-content',
    minWidth: '100%'
  },
  productContainer: {
    display: 'flex',
    gap: '16px'
  },
  productInfoColumn: {
    minWidth: COLUMN_WIDTHS.productInfo,
    flex: 1
  },
  qtyColumn: {
    width: COLUMN_WIDTHS.qty,
    flexShrink: 0
  },
  unitPriceColumn: {
    width: COLUMN_WIDTHS.unitPrice,
    flexShrink: 0
  },
  gpColumn: {
    width: COLUMN_WIDTHS.gp,
    flexShrink: 0
  },
  totalPriceColumn: {
    width: COLUMN_WIDTHS.totalPrice,
    flexShrink: 0
  },
  actionColumn: {
    width: COLUMN_WIDTHS.action,
    flexShrink: 0
  },
  noteContainer: {
    marginLeft: '80px',
    paddingTop: '12px',
    borderTop: '1px solid #E2E2EA'
  }
}));

interface ProductRowProps {
  product: QuoteProductDTO;
  searchQuery?: string;
  isSectionChild?: boolean;
  onProductClick?: (product: QuoteProductDTO) => void;
  scrollContainerRef?: RefObject<HTMLDivElement>;
  productStartOffset?: number;
  isRfqQuote?: boolean;
}

export const ProductRow = memo(
  ({
    product,
    searchQuery,
    isSectionChild,
    onProductClick,
    scrollContainerRef,
    productStartOffset,
    isRfqQuote
  }: ProductRowProps) => {
    const classes = useStyles();
    const productId = product?.id || product?._id;
    const notEditableTooltip = 'Quote is not editable';

    const {
      menuItems,
      isQuoteEditable,
      handleQuantityChange,
      handleSellChange,
      handleProfitMarginChange,
      handleProfitMarginPercentageChange
    } = useProductMenuItems(product);

    const optionHandlers = useOptionHandlers({
      productId,
      handleQuantityChange,
      handleSellChange,
      handleProfitMarginChange,
      handleProfitMarginPercentageChange
    });

    return (
      <Box className={classes.rowContainer}>
        <Box className={classes.productContainer}>
          {/* Product Info Column */}
          <Box className={classes.productInfoColumn}>
            <ProductInfo
              badgeText={product?.labelType}
              product={product}
              searchQuery={searchQuery}
              isRfqQuote={isRfqQuote}
              onClick={
                onProductClick ? () => onProductClick(product) : undefined
              }
            />
          </Box>

          {/* Quantity Column */}
          <Box className={classes.qtyColumn}>
            <QuantityColumn
              product={product}
              productId={productId}
              onQuantityChange={handleQuantityChange}
              disabled={!isQuoteEditable}
              disabledTooltip={notEditableTooltip}
            />
          </Box>

          {/* Unit Price Column */}
          <Box className={classes.unitPriceColumn}>
            <SellColumn
              product={product}
              productId={productId}
              onSellChange={handleSellChange}
              disabled={!isQuoteEditable}
              disabledTooltip={notEditableTooltip}
            />
          </Box>

          {/* GP Column */}
          <Box className={classes.gpColumn}>
            <GpColumn
              product={product}
              productId={productId}
              onProfitMarginChange={handleProfitMarginChange}
              onProfitMarginPercentageChange={
                handleProfitMarginPercentageChange
              }
              disabled={!isQuoteEditable}
              disabledTooltip={notEditableTooltip}
            />
          </Box>

          {/* Total Price Column */}
          <Box className={classes.totalPriceColumn}>
            <CostColumn product={product} />
          </Box>

          {/* Action Column */}
          <Box
            className={classes.actionColumn}
            style={
              isSectionChild
                ? { width: `calc(${COLUMN_WIDTHS.action} - 12px)` }
                : undefined
            }>
            <RowContainer>
              <FirstRowBox>
                <SDropdownMenu
                  newDesign
                  disabled={!isQuoteEditable}
                  disabledTooltip={notEditableTooltip}
                  menuItems={menuItems}
                />
              </FirstRowBox>
              <SecondRowBox hasNote={!!product?.note} />
            </RowContainer>
          </Box>
        </Box>

        {/* Child Products (Options) */}
        <ProductOptions
          product={product}
          isSectionChild={isSectionChild}
          searchQuery={searchQuery}
          onProductClick={onProductClick}
          scrollContainerRef={scrollContainerRef}
          productStartOffset={productStartOffset}
          isQuoteEditable={isQuoteEditable}
          optionHandlers={optionHandlers}
          isRfqQuote={isRfqQuote}
        />
      </Box>
    );
  }
);
